import { Injectable } from '@angular/core';
import { Assistance } from '../../models/assistance';
import { ExtendSession } from '../../models/extend-session';
import { Alert, AlertType } from '../../models/alert';
import { RestApiService } from '../../services/RestApiServices.service';
import { RealtimeService } from '../../services/realtime.service';
import { environment } from 'src/environments/environment';
import { BehaviorSubject } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { MiniLoaderService } from '../mini-loader/mini-loader.service';
import { ToastService } from '../toast/toast.service';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  public extendMinutes$ = new BehaviorSubject<ExtendSession>(null as any);

  private _notificationCounts: number;
  public get notificationCounts(): number {
    return this._notificationCounts;
  }
  public set notificationCounts(v: number) {
    this._notificationCounts = v;
  }

  private _notifications: Alert[];
  public get getNotifications(): Alert[] {
    return this._notifications;
  }

  notificationGranted: string;

  constructor(
    private realtimeService: RealtimeService,
    private restApiService: RestApiService,
    private translate: TranslateService,
    public loaderService: MiniLoaderService,
    private toastService: ToastService,
    private router: Router,
  ) {
    this._notificationCounts = 0;
    this.initialNotifications();
    this.notificationGranted = 'default';
  }

  public setNotifications(notif: Alert[]): void {
    this._notifications = notif;
    this.updateNotifications();
  }

  public setNewNotification(
    extendSession: ExtendSession,
    assistance: Assistance,
    sendAlert = false,
  ): void {
    var alert: Alert = new Alert({
      assistance: null,
      extendSession: null,
      type: null,
    });

    if (assistance) {
      for (let index = 0; index < this._notifications.length; index++) {
        if (
          this._notifications[index].assistance &&
          this._notifications[index].assistance.simId === assistance.simId
        ) {
          return;
        }
      }

      this.restApiService
        .getSimulatorByIdentifier(assistance.simId)
        .subscribe((simulator) => {
          if (!simulator) return;

          console.log('simulator details loaded');

          // for (let i = 0; i < locations.length; i++) {
          //   const loc = locations[i];
          //   for (let j = 0; j < loc.simulators.length; j++) {
          //     const s = loc.simulators[j];
          //     if (s.simulatorIdentifier == assistance.simId) {

          //     }
          //   }
          // }

          const body =
            this.translate.instant('assistanceNeededFor') + simulator.name;
          this.toastService.info(body);
          this.browserNotification('Bay Manager', body, {
            tag: 'assistance-' + assistance.simId,
            route: 'simulators',
          });

          alert.assistance = assistance;
          alert.extendSession = null;
          alert.type = AlertType.assistance;
          alert.alertBody = body;

          this.addNotifications(alert);
        });
    } else if (extendSession) {
      for (let index = 0; index < this._notifications.length; index++) {
        if (
          this._notifications[index].extendSession &&
          this._notifications[index].extendSession.eventId ===
            extendSession.eventId
        ) {
          this._notifications[index].extendSession.minutes =
            extendSession.minutes;

          this.updateNotifications();
          // this.filterNotification(notification.eventId);
          // break;
          return;
        }
      }

      if (sendAlert) {
        const body =
          this.translate.instant('sessionExtensionRequestFor') +
          extendSession.eventName;
        this.toastService.info(body);
        this.browserNotification('Bay Manager', body, {
          tag: 'extension-' + extendSession.eventId,
          route: 'event',
        });
      }

      alert.assistance = null;
      alert.extendSession = extendSession;
      alert.type = AlertType.extendSession;

      this.addNotifications(alert);
    }
    //this._notifications.unshift(alert);
  }

  /**
   * Filter notifications
   *
   * @param eventId
   */
  public filterNotification(eventId: number, simId, type: AlertType): void {
    for (let i = 0; i < this._notifications.length; i++) {
      const notif = this._notifications[i];
      if (
        type == this._notifications[i].type &&
        type == AlertType.extendSession
      ) {
        if (this._notifications[i].extendSession.eventId === eventId) {
          this._notifications.splice(i, 1);
          this.updateNotifications();
        }
      } else if (
        type == this._notifications[i].type &&
        type == AlertType.assistance
      ) {
        if (this._notifications[i].assistance.simId === simId) {
          this._notifications.splice(i, 1);
          this.updateNotifications();
        }
      }
      this._notificationCounts--;
    }
  }

  /**
   * Remove notifications from list when delete or time over event
   *
   * @param eventId
   */
  public removeNotification(simId: number, eventId: number): void {
    if (!this._notifications || !this._notifications.length) return;

    let notifLen = this._notifications.length;
    for (let index = 0; index < notifLen; index++) {
      if (
        this._notifications[index].simulator &&
        this._notifications[index].simulator.id === simId
      ) {
        let sim = this._notifications[index].simulator;
        if (typeof sim !== 'undefined' && typeof sim !== null) {
          let event = sim.events.find((f) => f.id === eventId);
          if (typeof event !== 'undefined' && typeof event !== null) {
            this._notifications = this._notifications.filter((e, i) => {
              return index !== i;
            });
            this.updateNotifications();
            return;
          }
        }
      }
    }
  }

  public get notificationsSupported(): boolean {
    return typeof window !== 'undefined' && 'Notification' in window;
  }

  public get notificationPermission(): string {
    return this.notificationsSupported ? Notification.permission : 'unsupported';
  }

  public requestAndShowPermission() {
    var isMobile = /iPhone|iPad|iPod|Android|Mobile/i.test(navigator.userAgent);
    if (isMobile || !this.notificationsSupported) return;
    if (Notification.permission === 'denied') {
      this.toastService.info(
        this.translate.instant('enableNotificationsInBrowser'),
      );
      return;
    }
    var self = this;
    Notification.requestPermission(function (permission) {
      self.notificationGranted = permission;
    });
  }

  public browserNotification(
    title: string,
    body: string,
    opts?: { tag?: string; route?: string },
  ) {
    if (!this.notificationsSupported || Notification.permission !== 'granted') {
      return;
    }
    if (document.hasFocus()) return;

    const options: any = {
      body,
      icon: 'favicon.ico',
      tag: opts?.tag,
      renotify: !!opts?.tag,
      requireInteraction: true,
    };
    const notification = new Notification(title, options);
    notification.onclick = () => {
      window.focus();
      if (opts?.route) this.router.navigate([opts.route]);
      notification.close();
    };
  }

  public async onApproved(extendSignal: ExtendSession): Promise<void> {
    await this.sendExtensionDecision(extendSignal.eventId, true);
  }

  public async onReject(extendSignal: ExtendSession): Promise<void> {
    await this.sendExtensionDecision(extendSignal.eventId, false);
  }

  private async sendExtensionDecision(
    eventId: number,
    approved: boolean,
  ): Promise<void> {
    try {
      this.loaderService.loader = true;

      await this.realtimeService.sendExtensionDecision(eventId, approved);
      window.localStorage.removeItem(environment.bayManagerUser);

      this.toastService.success(
        this.translate.instant(
          approved ? 'sessionTimeWasUpdated' : 'sessionTimeRequestWasRejected',
        ),
      );
    } catch (error) {
      console.error(error);
    } finally {
      this.loaderService.loader = false;
    }
  }

  public removeExtendSessionsByEventId(eventId: number): void {
    let updateNotifs = this.getNotifications.filter(
      (f) => f.extendSession?.eventId !== eventId,
    );
    this.setNotifications(updateNotifs);
  }

  public dismissAssistance(assistance: Assistance) {
    if (assistance.simId && assistance.simId.length)
      window.localStorage.removeItem('_fs_alert_' + assistance.simId);

    if (assistance.simId) {
      void this.realtimeService.resolveAssistance(assistance.simId);
    }

    this.filterNotification(null, assistance.simId, AlertType.assistance);
  }

  public removeAssistanceBySimId(simId: string): void {
    this.filterNotification(null, simId, AlertType.assistance);
  }

  public initialNotifications(): void {
    let storedNotifs = window.localStorage.getItem('Bay-Manager-Notifs');

    if (storedNotifs && storedNotifs.length) {
      this._notifications = JSON.parse(storedNotifs);
      this._notificationCounts = this._notifications.length;
    } else {
      this._notifications = new Array<Alert>();
    }
  }

  public hasRefreshNotification(notifications?: Alert[]): boolean {
    if (!notifications?.length) notifications = this._notifications;

    return !!notifications?.find(
      (f) => f.type === AlertType.refreshApplication,
    );
  }

  public addNotifications(notif: Alert): void {
    let storedNotifs = window.localStorage.getItem('Bay-Manager-Notifs'),
      notifications: Alert[];

    if (storedNotifs && storedNotifs.length) {
      notifications = JSON.parse(storedNotifs);
    } else {
      notifications = new Array<Alert>();
    }

    const isRefreshType = notif.type === AlertType.refreshApplication;

    if (isRefreshType) {
      if (this.hasRefreshNotification(notifications)) return;
    }

    notifications.unshift(notif);
    this._notifications.unshift(notif);
    this._notificationCounts++;

    if (isRefreshType) return;

    window.localStorage.setItem(
      'Bay-Manager-Notifs',
      JSON.stringify(notifications),
    );
  }

  public updateNotifications(): void {
    window.localStorage.setItem(
      'Bay-Manager-Notifs',
      JSON.stringify(this._notifications),
    );
    this._notificationCounts = this._notifications.length;
  }

  public resetNotifications(): void {
    window.localStorage.removeItem('Bay-Manager-Notifs');
    this._notifications = new Array<Alert>();
    this._notificationCounts = 0;
  }

  public refreshApplication(): void {
    window.location.reload();
  }
}
